/**
 * WhyPanel — auto-classification "why?" panel (task 204 D6).
 *
 * SlidePanel-hosted five-section view of a single suggestion row:
 *
 *   1. The call — project + confidence state + Accept / Override / Dismiss.
 *   2. What I saw — up to five plain-language bullets from `signals.perSignal`.
 *   3. Reasoning — LLM rationale, truncated to 240 chars with Read more.
 *   4. Evidence — inline screenshot crop, click → ScreenshotLightbox.
 *   5. Override-counter (conditional, per-employee only) — "you've
 *      overridden this N times" when N > 0.
 *
 * Plus an admin-gated Diagnostics expander with raw signal scores,
 * `model_version`, and `ai_call_log_id`. The admin gate is binary on the
 * `diagnosticsEnabled` prop — the parent surface decides (the operator
 * surface passes `true`; app-title-log passes `false`). Backend gates the
 * fields too, so `diagnosticsEnabled=true` is safe to set unconditionally
 * if the parent prefers.
 *
 * The composite is presentational. Data fetching (suggestion row,
 * screenshot signed URL, promote mutation) happens via the data-source
 * hooks the parent passes the result of, or via {@link usePromoteSuggestion}
 * for the action buttons. Never reads `dateRange` (DESIGN-RULES Rule 35
 * — not a date-window surface; reads suggestion id only).
 */

import { useMemo, useState } from 'react';
import { Button } from '@/components/primitives/Button';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { ScreenshotLightbox } from '@/components/composites/ScreenshotLightbox';
import { StatusDot } from '@/components/primitives/StatusDot';
import { useScreenshotUrl } from '@/hooks/useScreenshotUrl';
import { usePromoteSuggestion } from '@/hooks/useSuggestion';
import { buildWhatISawBullets } from '@/lib/whyPanelBullets';
import {
  deriveConfidenceState,
  dotStyleFor,
  type RuleCandidate,
  type SuggestionResponse,
} from '@/types/suggestion';
import styles from './WhyPanel.module.css';

const REASONING_TRUNCATE = 240;

export interface WhyPanelProps {
  open: boolean;
  onClose: () => void;
  /** The suggestion to render. `null` renders nothing (the surrounding
   *  panel won't show either). */
  suggestion: SuggestionResponse | null;
  /** When true, the panel shows the Diagnostics expander. Parent decides
   *  based on user role / surface; the backend also gates the relevant
   *  fields server-side. */
  diagnosticsEnabled?: boolean;
  /** Anchor event for the screenshot — falls back to
   *  `suggestion.eventId` if absent. Provided so the lightbox label
   *  can include the employee + timestamp from the parent's row context. */
  context?: {
    occurredAt: string;
    employeeName: string | null;
    tz: string;
  };
  /** Optional "open the override picker" handler — when omitted, the
   *  Override button is hidden (parents that don't want override flow
   *  inline can omit). The parent receives the suggestion and decides
   *  how to render the project picker. */
  onOverrideRequest?: (suggestion: SuggestionResponse) => void;
  /** Optional callback fired after a successful promote action — the
   *  parent uses this to surface the PatternExtractionPrompt when the
   *  response carries a non-null `ruleCandidate`. */
  onSettled?: (args: {
    action: 'accept' | 'override' | 'dismiss';
    ruleCandidate: RuleCandidate | null;
  }) => void;
}

export function WhyPanel({
  open,
  onClose,
  suggestion,
  diagnosticsEnabled = false,
  context,
  onOverrideRequest,
  onSettled,
}: WhyPanelProps) {
  const promote = usePromoteSuggestion();
  const [expanded, setExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  const confidenceState = useMemo(() => {
    if (!suggestion) return null;
    const emptySignals = Object.keys(suggestion.signals.perSignal).length === 0;
    return deriveConfidenceState({
      fused: suggestion.fused,
      emptySignals,
      // The frontend lane has no view into "target archived" — when the
      // backend wants to force unclassified for an archived project, it
      // can pass `targetProject: null`. Treat that as an extra signal.
      targetArchived: !suggestion.topProject,
    });
  }, [suggestion]);

  const bullets = useMemo(
    () => (suggestion ? buildWhatISawBullets(suggestion.signals.perSignal) : []),
    [suggestion],
  );

  // Refresh-on-stale signed URL: the hook keys on screenshotId and the
  // React Query staleTime is 50min (well inside the imgproxy TTL). When
  // the URL expires while the panel is open, useScreenshotUrl re-runs
  // and returns a fresh one — see hook docstring.
  const { url: screenshotUrl } = useScreenshotUrl({
    screenshotId: suggestion?.screenshotId ?? '',
    width: 480,
  });
  // Don't ask for a URL when there is no screenshot id.
  const evidenceUrl = suggestion?.screenshotId ? screenshotUrl : null;

  if (!suggestion || !confidenceState) {
    return (
      <SlidePanel open={open} onClose={onClose} title="Why this guess?" width="md">
        <p className={styles.empty}>No suggestion is attached to this event.</p>
      </SlidePanel>
    );
  }

  const dot = dotStyleFor(confidenceState);
  const isLong = (suggestion.reasoning?.length ?? 0) > REASONING_TRUNCATE;
  const shownReasoning = expanded
    ? suggestion.reasoning
    : (suggestion.reasoning?.slice(0, REASONING_TRUNCATE) ?? null);

  const isBusy = promote.isPending;

  const handleAccept = async () => {
    const res = await promote.mutateAsync({
      eventId: suggestion.eventId,
      action: 'accept',
      suggestionId: suggestion.id,
    });
    onSettled?.({ action: 'accept', ruleCandidate: res.ruleCandidate });
    onClose();
  };

  const handleOverride = () => {
    if (onOverrideRequest) {
      onOverrideRequest(suggestion);
      return;
    }
    // Without a parent-provided picker, the inline override path is a
    // no-op — the button is hidden in that case, but defensive guard.
  };

  const handleDismiss = async () => {
    const res = await promote.mutateAsync({
      eventId: suggestion.eventId,
      action: 'dismiss',
      suggestionId: suggestion.id,
    });
    onSettled?.({ action: 'dismiss', ruleCandidate: res.ruleCandidate });
    onClose();
  };

  return (
    <>
      <SlidePanel
        open={open}
        onClose={onClose}
        title="Why this guess?"
        subtitle={
          suggestion.topProject
            ? `${suggestion.topProject.name} · ${confidenceLabel(confidenceState)}`
            : confidenceLabel(confidenceState)
        }
        width="md"
      >
        <section className={styles.section} aria-labelledby="why-call">
          <h3 id="why-call" className={styles.sectionTitle}>
            The call
          </h3>
          <div className={styles.callRow}>
            <StatusDot tone={dot.tone} shape={dot.shape} />
            <span className={styles.callCopy}>{dot.copy}</span>
          </div>
          <div className={styles.actions}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleAccept()}
              disabled={isBusy}
              data-testid="why-panel-accept"
            >
              Accept
            </Button>
            {onOverrideRequest && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleOverride}
                disabled={isBusy}
                data-testid="why-panel-override"
              >
                Override
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleDismiss()}
              disabled={isBusy}
              data-testid="why-panel-dismiss"
            >
              Dismiss
            </Button>
          </div>
        </section>

        <section className={styles.section} aria-labelledby="why-what-i-saw">
          <h3 id="why-what-i-saw" className={styles.sectionTitle}>
            What I saw
          </h3>
          {bullets.length === 0 ? (
            <p className={styles.empty}>No signals were strong enough to call out.</p>
          ) : (
            <ul className={styles.bullets}>
              {bullets.map((b) => (
                <li key={b.key} className={styles.bullet}>
                  {b.bullet}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.section} aria-labelledby="why-reasoning">
          <h3 id="why-reasoning" className={styles.sectionTitle}>
            Reasoning
          </h3>
          {suggestion.reasoning ? (
            <p className={styles.reasoning}>
              {shownReasoning}
              {isLong && !expanded && (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setExpanded(true)}
                  data-testid="why-panel-read-more"
                >
                  {' '}
                  Read more
                </button>
              )}
              {isLong && expanded && (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setExpanded(false)}
                >
                  {' '}
                  Show less
                </button>
              )}
            </p>
          ) : (
            <p className={styles.empty}>No rationale recorded for this guess.</p>
          )}
        </section>

        <section className={styles.section} aria-labelledby="why-evidence">
          <h3 id="why-evidence" className={styles.sectionTitle}>
            Evidence
          </h3>
          {suggestion.screenshotId && evidenceUrl ? (
            <button
              type="button"
              className={styles.evidenceButton}
              onClick={() => setLightboxOpen(true)}
              aria-label="Open screenshot full size"
              data-testid="why-panel-evidence"
            >
              <img
                src={evidenceUrl}
                alt="Screenshot at the moment this event was captured"
                className={styles.evidence}
              />
            </button>
          ) : (
            <p className={styles.empty}>No screenshot is attached to this event.</p>
          )}
        </section>

        {suggestion.overrideCount > 0 && (
          <section
            className={styles.section}
            aria-labelledby="why-override-counter"
            data-testid="why-panel-override-counter"
          >
            <h3 id="why-override-counter" className={styles.sectionTitle}>
              History
            </h3>
            <p className={styles.counter}>
              {/* Per-employee scope — "you've overridden", never "this has
                  been overridden N times across your org". */}
              You&rsquo;ve overridden this {suggestion.overrideCount}{' '}
              {suggestion.overrideCount === 1 ? 'time' : 'times'}.
            </p>
          </section>
        )}

        {diagnosticsEnabled && (
          <section className={styles.section} data-testid="why-panel-diagnostics">
            <button
              type="button"
              className={styles.diagToggle}
              aria-expanded={diagOpen}
              onClick={() => setDiagOpen((v) => !v)}
            >
              <span className={styles.sectionTitle}>Diagnostics</span>
              <span aria-hidden="true">{diagOpen ? '−' : '+'}</span>
            </button>
            {diagOpen && (
              <dl className={styles.diagList}>
                <div className={styles.diagRow}>
                  <dt>Fused score</dt>
                  <dd className={styles.mono}>{suggestion.fused.toFixed(3)}</dd>
                </div>
                <div className={styles.diagRow}>
                  <dt>Weights version</dt>
                  <dd className={styles.mono}>{suggestion.signals.weightsVersion}</dd>
                </div>
                {suggestion.modelVersion && (
                  <div className={styles.diagRow}>
                    <dt>Model</dt>
                    <dd className={styles.mono}>{suggestion.modelVersion}</dd>
                  </div>
                )}
                {suggestion.aiCallLogId && (
                  <div className={styles.diagRow}>
                    <dt>AI call log id</dt>
                    <dd className={styles.mono}>{suggestion.aiCallLogId}</dd>
                  </div>
                )}
                <div className={styles.diagRow}>
                  <dt>Per-signal scores</dt>
                  <dd>
                    <pre className={styles.diagPre}>
                      {JSON.stringify(suggestion.signals.perSignal, null, 2)}
                    </pre>
                  </dd>
                </div>
              </dl>
            )}
          </section>
        )}
      </SlidePanel>

      {suggestion.screenshotId && context && (
        <ScreenshotLightbox
          open={lightboxOpen}
          entries={[
            {
              screenshotId: suggestion.screenshotId,
              occurredAt: context.occurredAt,
              employeeName: context.employeeName,
              altText: 'Screenshot at the moment this event was captured',
            },
          ]}
          index={0}
          tz={context.tz}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => {}}
          onNext={() => {}}
        />
      )}
    </>
  );
}

function confidenceLabel(state: ReturnType<typeof deriveConfidenceState>): string {
  switch (state) {
    case 'settled':
      return 'High confidence';
    case 'worth-a-glance':
      return 'Worth a glance';
    case 'unclassified':
      return 'Unclassified';
  }
}
